"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.saveRateAssignmentRequestSchema = exports.saveRateTemplateRequestSchema = exports.rateAssignmentListSchema = exports.rateAssignmentSchema = exports.RATE_SOURCES = exports.rateCardTemplateSchema = exports.ratePeriodSchema = exports.RATE_UNITS = void 0;
exports.periodOn = periodOn;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Rate cards — C51, Phase 4. What an hour costs, and what it is sold for.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME SHAPE AS A WORK SCHEDULE, DELIBERATELY.** A workspace curates **templates** with
 * periods; a person is given **assignments**, each from a date. `schedules.ts` is the sibling and
 * C43's ruling applies word for word: a person's rate is *a timeline, not a field*, because a cost
 * figure for February has to stay what it was after a raise in March.
 *
 * ⚠️⚠️ **TWO RATES, AND THE GAP BETWEEN THEM IS THE MARGIN.** `cost` is the internal figure,
 * `target` the billed one — *"a költségdíj a belső önköltség, a céldíj a kiszámlázott ár"*.
 * Resourcing reads the first, billing the second.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const codeSchema = zod_1.z.string().regex(/^[A-Z]{3}$/);
/** ⚠️ Hourly and daily are both ordinary — a nearshore tester and a PM in the mockup's own seed. */
exports.RATE_UNITS = ['hour', 'day'];
exports.ratePeriodSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `rate_period_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('ratePeriod'),
    /** From this day, this is the price — until the next period's `fromDate`. There is no end. */
    fromDate: wireDateSchema,
    unit: zod_1.z.enum(exports.RATE_UNITS),
    currencyCode: codeSchema,
    /** ⚠️ Strings: `numeric` crosses the driver as one. Parse before arithmetic. */
    cost: zod_1.z.string(),
    target: zod_1.z.string(),
});
exports.rateCardTemplateSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `rate_card_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('rateCard'),
    /** The quotable number the screen shows — `4101`. */
    number: zod_1.z.number().int(),
    name: zod_1.z.string(),
    active: zod_1.z.boolean(),
    periods: zod_1.z.array(exports.ratePeriodSchema),
    /**
     * ⚠️ **How many people are on it**, which the list draws under the name as *"2 időszak · 1 fő"*
     * — and which is what makes deleting one a decision rather than a click.
     */
    assignedCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
/** Which arm an assignment is on — a template, or a rate of this person's own. */
exports.RATE_SOURCES = ['template', 'custom'];
/**
 * One person's rate from one date.
 *
 * ⚠️⚠️ **THE RESOLVED FIGURES TRAVEL WITH IT, WHICH IS WHY THE LIST NEEDS NO SECOND READ.** A
 * `template` assignment carries the template's own name and the period in force; a `custom` one
 * carries what was typed. The screen draws one set of columns over both.
 */
exports.rateAssignmentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `rate_assignment_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('rateAssignment'),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    memberAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    fromDate: wireDateSchema,
    source: zod_1.z.enum(exports.RATE_SOURCES),
    /** Null on a `custom` row. */
    templateId: zod_1.z.string().nullable(),
    templateName: zod_1.z.string().nullable(),
    /**
     * ⚠️ **What applies TODAY**, resolved server-side through the template's periods. Null when a
     * template has no period that has started yet — a card somebody set up for next year.
     */
    unit: zod_1.z.enum(exports.RATE_UNITS).nullable(),
    currencyCode: codeSchema.nullable(),
    cost: zod_1.z.string().nullable(),
    target: zod_1.z.string().nullable(),
    version: zod_1.z.number().int(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE ROW PER ASSIGNMENT — and `missing` is who has no rate TODAY.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS SHIPPED AS ONE ROW PER PERSON AND THAT WAS WRONG, TWICE OVER.** It collapsed to the
 * rate in force today, on the mockup's shape — and the product owner ruled it out in the same
 * words the work schedules got in September: *"one row for one user should be one period, and
 * adding another creates another row"*. A rate is a TIMELINE (`0127`); a screen showing one point
 * on it makes a raise booked for next year invisible, makes the row it replaced vanish, and leaves
 * neither correctable.
 *
 * ⚠️ **So a person appears as many times as they have assignments, and `missing` still answers a
 * different question**: who has no rate that has STARTED. Somebody whose only assignment begins
 * next quarter is in both — once as an unpriced person, once as a future row — which is the honest
 * pair rather than a contradiction.
 *
 * ⚠️ **`missing` is *nobody's rate has started*, not *nobody has a row*.** A person whose only
 * assignment begins next quarter is unpriced today and belongs there — read the other way they
 * would appear in neither array, which is the one state this tab exists to make impossible. The
 * mockup's own header counts the two: *"6 dolgozónak van díja · 0 hiányzik"*.
 */
exports.rateAssignmentListSchema = zod_1.z.object({
    assignments: zod_1.z.array(exports.rateAssignmentSchema),
    /** Members with no rate IN FORCE today, in display order. */
    missing: zod_1.z.array(zod_1.z.object({
        memberId: zod_1.z.string(),
        memberName: zod_1.z.string(),
        memberAvatarUrl: zod_1.z.string().nullable(),
        memberAvatarSeed: zod_1.z.string().nullable(),
    })),
});
const periodInputSchema = zod_1.z.object({
    fromDate: wireDateSchema,
    unit: zod_1.z.enum(exports.RATE_UNITS),
    currencyCode: codeSchema,
    cost: zod_1.z.number().min(0),
    target: zod_1.z.number().min(0),
});
exports.saveRateTemplateRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    active: zod_1.z.boolean(),
    /**
     * ⚠️ **The whole set, every time.** A template's periods are edited together in one dialog — the
     * mockup adds and removes rows before saving — so a partial update would need a diff the form
     * does not produce. At least one: a card with no period prices nothing.
     */
    periods: zod_1.z.array(periodInputSchema).min(1),
    version: zod_1.z.number().int().optional(),
});
/**
 * ⚠️ **The two arms, as the table's own constraint spells them.** `template` names a card;
 * `custom` carries the rate. A shape with both would make the reader choose, which is the ambiguity
 * `source` exists to remove.
 */
exports.saveRateAssignmentRequestSchema = zod_1.z.discriminatedUnion('source', [
    zod_1.z.object({
        source: zod_1.z.literal('template'),
        memberId: zod_1.z.string(),
        fromDate: wireDateSchema,
        templateId: zod_1.z.string(),
        version: zod_1.z.number().int().optional(),
    }),
    zod_1.z.object({
        source: zod_1.z.literal('custom'),
        memberId: zod_1.z.string(),
        fromDate: wireDateSchema,
        unit: zod_1.z.enum(exports.RATE_UNITS),
        currencyCode: codeSchema,
        cost: zod_1.z.number().min(0),
        target: zod_1.z.number().min(0),
        version: zod_1.z.number().int().optional(),
    }),
]);
/**
 * The period in force on a date.
 *
 * ⚠️⚠️ **THE LAST ONE THAT HAS STARTED, WHICH IS `0107`'s RULE AND NOT A MAXIMUM.** Periods are
 * ordered by their start; the answer is the latest whose `fromDate` is on or before the day. A
 * card whose first period starts next year prices nothing today, and `null` is the honest answer
 * rather than the first period.
 */
function periodOn(periods, onDate) {
    let found = null;
    for (const period of periods) {
        if (period.fromDate <= onDate && (found === null || period.fromDate > found.fromDate)) {
            found = period;
        }
    }
    return found;
}
//# sourceMappingURL=rate-card.js.map